import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return matchesFilterResult && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getSelectedChatAttachmentsLoaded: ({ selectedChatId, attachments }) =>
    selectedChatId !== null && attachments[selectedChatId] !== undefined,
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      const isLumerAIFlow =
        conversation.custom_attributes &&
        conversation.custom_attributes.active_lumerai_flow_id;
      const isLumerAIAutopilot =
        conversation.custom_attributes &&
        conversation.custom_attributes.ai_autopilot_status === 'active';
      const isCampaign = conversation.status === 'campaign';
      const isUnAssigned =
        !conversation.meta.assignee &&
        !isCampaign &&
        !isLumerAIFlow &&
        !isLumerAIAutopilot;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
  },
  getBotChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      const isAssignedToHuman =
        !!conversation.meta?.assignee || !!conversation.meta?.team;
      const isLumerAIFlow =
        conversation.custom_attributes &&
        conversation.custom_attributes.active_lumerai_flow_id;
      const isLumerAIAutopilot =
        conversation.custom_attributes &&
        conversation.custom_attributes.ai_autopilot_status === 'active';
      const isPending =
        conversation.status === 'pending' ||
        !!isLumerAIFlow ||
        !!isLumerAIAutopilot;
      const modifiedFilters = { ...activeFilters, status: 'all' };
      const shouldFilter = applyPageFilters(conversation, modifiedFilters);
      return !isAssignedToHuman && isPending && shouldFilter;
    });
  },
  getAssignedToOthersChats: _state => activeFilters => {
    const filters = { ...activeFilters, status: 'all' };

    return _state.allConversations.filter(conversation => {
      const assigneeId = conversation.meta?.assignee?.id;
      const isAssigned = Boolean(assigneeId);
      const isCampaign = conversation.status === 'campaign';
      return (
        isAssigned && !isCampaign && applyPageFilters(conversation, filters)
      );
    });
  },
  getCampaignsChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      const isCampaign = conversation.status === 'campaign';
      const modifiedFilters = { ...activeFilters, status: 'all' };
      const shouldFilter = applyPageFilters(conversation, modifiedFilters);
      return isCampaign && shouldFilter;
    });
  },
  getParticipatingChats: _state => activeFilters => {
    // The participating endpoint already scopes the payload to the current
    // user. Do not filter it again with the watchers cache: that cache is
    // populated only for opened conversations and an empty entry is not proof
    // that the user is not participating.
    const filters = ['all', 'assigned_to_others', 'bot', 'campaigns'].includes(
      activeFilters.assigneeType
    )
      ? { ...activeFilters, status: 'all' }
      : activeFilters;

    return _state.allConversations.filter(conversation =>
      applyPageFilters(conversation, filters)
    );
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);
    // The "Todos" tab contains active conversations only. Resolved
    // conversations remain available through the resolved status filter.
    const allStatusesFilters = { ...activeFilters, status: 'all' };
    const activeStatuses = ['open', 'pending', 'snoozed', 'campaign'];

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, allStatusesFilters);
      const isActive = activeStatuses.includes(conversation.status);
      const allowedForRole = applyRoleFilter(
        conversation,
        userRole,
        permissions,
        currentUserId
      );

      return shouldFilter && isActive && allowedForRole;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
